import React, { useCallback, useEffect, useRef } from 'react';

import { MaxVideoDurationSeconds } from 'js/src/client';
import CheckPermissionsError from 'js/src/components/app/recorder/CheckPermissionsError';
import UnsupportedContent from 'js/src/components/app/recorder/CheckSupportError';
import CountdownTimer from 'js/src/components/app/recorder/CountdownTimer';
import DeviceSelectorButton from 'js/src/components/app/recorder/DeviceSelectorButton';
import DeviceSelectorControls from 'js/src/components/app/recorder/DeviceSelectorControls';
import DurationWarning from 'js/src/components/app/recorder/DurationWarning';
import FinishRecordingButton from 'js/src/components/app/recorder/FinishRecordingButton';
import LivePreview from 'js/src/components/app/recorder/LivePreview';
import PauseRecordingButton from 'js/src/components/app/recorder/PauseRecordingButton';
import Prompter from 'js/src/components/app/recorder/Prompter';
import PrompterButton from 'js/src/components/app/recorder/PrompterButton';
import RecordedPreview from 'js/src/components/app/recorder/RecordedPreview';
import RecorderClock from 'js/src/components/app/recorder/RecorderClock';
import ResumeRecordingButton from 'js/src/components/app/recorder/ResumeRecordingButton';
import RetakeOverlay from 'js/src/components/app/recorder/RetakeOverlay';
import RetakeRecordingButton from 'js/src/components/app/recorder/RetakeRecordingButton';
import ScreenRecordingInstructions from 'js/src/components/app/recorder/ScreenRecordingInstructions';
import StartPage from 'js/src/components/app/recorder/StartPage';
import StartRecordingButton from 'js/src/components/app/recorder/StartRecordingButton';
import SystemAudioMissingWarning from 'js/src/components/app/recorder/SystemAudioMissingWarning';
import SystemAudioToggleButton from 'js/src/components/app/recorder/SystemAudioToggleButton';
import type { DeviceInfo, MediaRecorderState } from 'js/src/components/app/recorder/types';
import {
    CAMERA_OFF_DEVICE_ID,
    DeviceTypes,
    RECORD_MODAL_STATE,
} from 'js/src/components/app/recorder/types';
import UploadRecordingButton from 'js/src/components/app/recorder/UploadRecordingButton';
import useAudibleCountdown, {
    CountdownCancelReason,
} from 'js/src/components/app/recorder/useAudibleCountdown';
import { getMillisFromSeconds } from 'js/src/components/common/asset-trimmer/common';
import { Toast } from 'js/src/components/common/Toast';
import { type UploadRecordingType } from 'js/src/data/uploads';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { TranscriptWord } from 'js/src/libs/realtime-transcription/scribe-realtime-client';
import { ScribeRealtimeClient } from 'js/src/libs/realtime-transcription/scribe-realtime-client';
import type { BaseRecorder } from 'js/src/libs/recorder/base-recorder';
import { RECORDER_EVENTS, RecorderEventEmitter } from 'js/src/libs/recorder/recorder-event-emitter';
import { RecordingUploader } from 'js/src/libs/recorder/recording-uploader';
import { isError } from 'js/src/types';

export type UploadRecordingArgs = {
    gcsRef: { bucket: string; file_name: string; asyncOperationUuid: string };
    type: UploadRecordingType;
    recordedDuration: number;
    recordingMeta: { fileName: string; mimeType: string };
    /**
     * Word-timed transcript collected during recording via ElevenLabs Scribe
     * Realtime. Present only when the caller opted into live transcription.
     * The recipient should pass this through to
     * `UploadsOperations.uploadRecordingFromGCS` so the backend skips its own
     * batch transcription. The language is detected backend-side from the
     * transcript text — Scribe's realtime API doesn't reliably report it.
     */
    transcriptWords?: TranscriptWord[];
};

const COUNTDOWN_SECONDS = 3;
const DEFAULT_MAXIMUM_RECORDING_TIME = getMillisFromSeconds(MaxVideoDurationSeconds.LIMIT);
const DEFAULT_RECORDING_TIME_WARNING = getMillisFromSeconds(MaxVideoDurationSeconds.WARNING);

export type UseMediaRecorderOptions = {
    /** Hard cap; recording auto-finishes when exceeded. Defaults to 10 minutes. */
    maxDurationMs?: number;
    /** Shows the "approaching limit" warning callout. Defaults to ~9 minutes. */
    warningDurationMs?: number;
    /** When true, the prompter overlay is shown by default. */
    initialPrompterVisible?: boolean;
    /** Replaces the default prompter placeholder script. */
    prompterDefaultText?: string;
    /** Set when the prompter sits over a light stage rather than a camera/screen feed. */
    prompterOnLightBackground?: boolean;
    /**
     * Whether the device-selector controls include a camera dropdown. Defaults
     * to true; audio-only (voiceover) recording sets this to false.
     */
    showCameraDeviceSelector?: boolean;
    allowCameraOff?: boolean;
    showSystemAudioToggle?: boolean;
    /**
     * Capture-only mode: when provided, the recorder skips the streaming GCS
     * upload and live transcription entirely and instead hands the finished
     * recording's raw `Blob` back to this callback (used by voice cloning,
     * which sends the audio to the clone API rather than the media library).
     * `uploadType`/`onUploadRecording` are unused in this mode.
     */
    onRecordingComplete?: (blob: Blob) => void;
};

export const useMediaRecorder = (
    recorder: BaseRecorder,
    uploadType: UploadRecordingType,
    onUploadRecording: (args: UploadRecordingArgs) => void,
    options: UseMediaRecorderOptions = {},
) => {
    const {
        maxDurationMs = DEFAULT_MAXIMUM_RECORDING_TIME,
        warningDurationMs = DEFAULT_RECORDING_TIME_WARNING,
        initialPrompterVisible = false,
        prompterDefaultText,
        prompterOnLightBackground = false,
        showCameraDeviceSelector = true,
        allowCameraOff = false,
        showSystemAudioToggle = false,
        onRecordingComplete,
    } = options;

    const uploaderRef = useRef<RecordingUploader | null>(null);
    const uploaderStartPromiseRef = useRef<Promise<void> | null>(null);
    const isUploadingRef = useRef(false);
    const transcriberRef = useRef<ScribeRealtimeClient | null>(null);

    const [recordModalState, setRecordModalState] = React.useState(RECORD_MODAL_STATE.START);
    const [mediaRecorderState, setMediaRecorderState] =
        React.useState<MediaRecorderState>('inactive');

    const [showingPrompter, setShowingPrompter] = React.useState(initialPrompterVisible);
    const [showDurationWarning, setDurationWarning] = React.useState(false);
    const [showingRetakeOverlay, setShowingRetakeOverlay] = React.useState(false);
    const [liveStream, setLiveStream] = React.useState<MediaStream | null>(null);
    const [recordedPreviewUrl, setRecordedPreviewUrl] = React.useState<string | undefined>(
        undefined,
    );

    const [isUploading, setIsUploading] = React.useState(false);

    const [isCameraConnected, setIsCameraConnected] = React.useState(false);
    const [isMicrophoneConnected, setIsMicrophoneConnected] = React.useState(false);
    const [isSystemAudioEnabled, setIsSystemAudioEnabled] = React.useState(false);
    const [systemAudioMissing, setSystemAudioMissing] = React.useState(false);

    const [availableCameraDevices, setAvailableCameraDevices] = React.useState<DeviceInfo[]>([]);
    const [availableMicrophoneDevices, setAvailableMicrophoneDevices] = React.useState<
        DeviceInfo[]
    >([]);
    const [availableScreenDevices, setAvailableScreenDevices] = React.useState<DeviceInfo[]>([]);

    const [activeCameraDeviceId, setActiveCameraDeviceId] = React.useState<string | undefined>(
        undefined,
    );
    const [activeMicrophoneDeviceId, setActiveMicrophoneDeviceId] = React.useState<
        string | undefined
    >(undefined);
    const [activeScreenDeviceId, setActiveScreenDeviceId] = React.useState<string | undefined>(
        undefined,
    );

    const checkNotificationPermission = useCallback(async () => {
        if ('Notification' in window && Notification.permission === 'default') {
            await Notification.requestPermission();
        }
    }, []);

    const updateComponentStateFromRecorder = React.useCallback(() => {
        setMediaRecorderState(recorder.mediaRecorderState);
        setAvailableMicrophoneDevices(recorder.availableMicrophoneDevices);
        setAvailableCameraDevices(recorder.availableCameraDevices);
        setAvailableScreenDevices(recorder.availableScreenDevices);
        setActiveMicrophoneDeviceId(recorder.activeMicrophoneDeviceId);
        setActiveCameraDeviceId(recorder.activeCameraDeviceId);
        setActiveScreenDeviceId(recorder.activeScreenDeviceId);
        setIsMicrophoneConnected(recorder.isMicrophoneConnected);
        setIsCameraConnected(recorder.isCameraConnected);
        setIsSystemAudioEnabled(recorder.isSystemAudioCaptureEnabled);
        setLiveStream(recorder.liveStream);
    }, [recorder]);

    const handleSetupError = React.useCallback((error: unknown) => {
        let captureError = true;
        const errorName = error instanceof Error ? error.name : 'UnknownError';
        switch (errorName) {
            case 'AbortError':
            case 'InvalidStateError':
                Toast.error('Something went wrong while accessing your devices. Please try again.');
                break;
            case 'NotAllowedError':
                captureError = false;
                setRecordModalState(RECORD_MODAL_STATE.PERMISSION_DENIED);
                break;
            case 'NotFoundError':
                captureError = false;
                Toast.error(
                    'No camera or microphone found. Please connect a device and try again.',
                );
                break;
            case 'NotReadableError':
                captureError = false;
                Toast.error(
                    'Unable to access your camera or microphone. The device may already be in use.',
                );
                break;
            case 'OverconstrainedError':
                captureError = false;
                Toast.error(
                    'Unable to find a device that meets the requirements. Please try a different device.',
                );
                break;
            case 'TypeError':
                Toast.error('Invalid device configuration. Please try again.');
                break;
            // Special case for screen sharing cancellation
            case 'NotSharedError':
                captureError = false;
                break;
            default:
                Toast.error('An error occurred while setting up the recorder.');
                break;
        }
        if (captureError) {
            ErrorReporter.capture(error);
        }
    }, []);

    const onMount = React.useCallback(async () => {
        try {
            recorder.checkSupport();
        } catch (error) {
            setRecordModalState(RECORD_MODAL_STATE.UNSUPPORTED);
            return;
        }

        try {
            await recorder.checkPermissions();
            await checkNotificationPermission();
        } catch (error) {
            setRecordModalState(RECORD_MODAL_STATE.PERMISSION_DENIED);
            return;
        }

        try {
            await recorder.initialize();
        } catch (error) {
            ErrorReporter.capture(error);
            Toast.error('An error occurred while initializing the recorder.');
            return;
        }

        try {
            await recorder.connectCamera();
            await recorder.connectMicrophone();
        } catch (error) {
            handleSetupError(error);
            return;
        }

        setRecordModalState(RECORD_MODAL_STATE.READY);
        updateComponentStateFromRecorder();
    }, [checkNotificationPermission, handleSetupError, recorder, updateComponentStateFromRecorder]);

    const startPage = React.useMemo(
        () => <StartPage key="recorder-start-page" onMount={onMount} />,
        [onMount],
    );

    const checkSupportError = React.useMemo(
        () => <UnsupportedContent key="unsupported-content" />,
        [],
    );

    const checkPermissionsError = React.useMemo(
        () => <CheckPermissionsError key="check-permissions-error" />,
        [],
    );

    const emitRecordingStartedNotification = React.useCallback(() => {
        if ('Notification' in window && Notification.permission === 'granted') {
            const systemAudioUnavailable =
                recorder.isSystemAudioCaptureEnabled && !recorder.isSystemAudioActive;
            const body = systemAudioUnavailable
                ? "System audio isn't being captured — re-share with the audio toggle on."
                : undefined;
            try {
                new Notification('Recording started', body ? { body } : undefined);
            } catch (error) {
                // Some browsers (e.g. Chrome when the page is under an active
                // Service Worker scope) disallow the Notification constructor
                // even when permission is granted. This is a best-effort
                // courtesy notification, so a failure here must not abort the
                // recording-start flow in handleCountdownFinished.
                ErrorReporter.capture(error);
            }
        }
    }, [recorder]);

    const handleCountdownFinished = React.useCallback(async () => {
        try {
            await recorder.startRecording();
            emitRecordingStartedNotification();
            setRecordModalState(RECORD_MODAL_STATE.RECORDING);
            updateComponentStateFromRecorder();

            // Start live transcription now that the recording is running,
            // feeding Scribe the recorder's existing mic track so its t=0 lines
            // up with the recording.
            const micTrack = recorder.micTrack;
            if (!onRecordingComplete && micTrack) {
                const transcriber = new ScribeRealtimeClient(micTrack);
                transcriberRef.current = transcriber;
                void transcriber.start();
            }
        } catch (error) {
            Toast.error('An error occurred while starting the recording.');
            ErrorReporter.capture(error);
        }
    }, [
        emitRecordingStartedNotification,
        onRecordingComplete,
        recorder,
        updateComponentStateFromRecorder,
    ]);

    const { display: countdownDisplay, waitForCountdown } = useAudibleCountdown();

    const countdownTimer = React.useMemo(
        () => <CountdownTimer key="recorder-countdown-timer" count={countdownDisplay} />,
        [countdownDisplay],
    );

    const handleStartRecording = React.useCallback(async () => {
        try {
            /**
             * We can safely call `connectScreen` even if we are not using
             * screen recording, as all the different recorder classes extend
             * BaseRecorder which has a default implementation of `connectScreen`
             * that does nothing.
             */
            await recorder.connectScreen();
        } catch (error) {
            handleSetupError(error);
            return;
        }

        setSystemAudioMissing(
            recorder.isSystemAudioCaptureEnabled && !recorder.isSystemAudioActive,
        );

        updateComponentStateFromRecorder();
        setRecordModalState(RECORD_MODAL_STATE.COUNTDOWN);

        // Kick off the resumable GCS session in parallel with the countdown so
        // recording can start immediately when the countdown finishes — the
        // session-init API call usually completes in well under 3 seconds.
        if (!onRecordingComplete) {
            const uploader = new RecordingUploader(recorder);
            uploaderRef.current = uploader;
            uploaderStartPromiseRef.current = uploader.start().catch((error) => {
                ErrorReporter.capture(error);
                Toast.error('Failed to start the recording upload — please try again.');
                uploaderRef.current = null;
                uploaderStartPromiseRef.current = null;
                throw error;
            });
        }

        try {
            await waitForCountdown(COUNTDOWN_SECONDS);
        } catch (error: unknown) {
            if (uploaderRef.current) {
                uploaderRef.current.abort();
                uploaderRef.current = null;
                uploaderStartPromiseRef.current = null;
            }
            if (
                isError(error) &&
                error.message !== String(CountdownCancelReason.ComponentUnmounted)
            ) {
                Toast.error('An error occurred during the countdown.');
                ErrorReporter.capture(error);
            }
            return;
        }

        try {
            await uploaderStartPromiseRef.current;
        } catch {
            return; // toast already shown
        }

        await handleCountdownFinished();
    }, [
        handleCountdownFinished,
        handleSetupError,
        onRecordingComplete,
        recorder,
        updateComponentStateFromRecorder,
        waitForCountdown,
    ]);

    const startRecordingButton = React.useMemo(
        () => <StartRecordingButton key="start-recording-button" onClick={handleStartRecording} />,
        [handleStartRecording],
    );

    const finishRecording = React.useCallback(async () => {
        transcriberRef.current?.stopCapturing();
        const { previewUrl, state } = await recorder.finishRecording();
        setMediaRecorderState(state);
        setRecordedPreviewUrl(previewUrl);
        setRecordModalState(RECORD_MODAL_STATE.FINISH_RECORDING);
    }, [recorder]);

    const finishRecordingButton = React.useMemo(
        () => <FinishRecordingButton key="finish-recording-button" onClick={finishRecording} />,
        [finishRecording],
    );

    const confirmRetakeRecording = React.useCallback(() => {
        setShowingRetakeOverlay(true);
    }, []);

    const retakeRecordingButton = React.useMemo(
        () => (
            <RetakeRecordingButton key="retake-recording-button" onClick={confirmRetakeRecording} />
        ),
        [confirmRetakeRecording],
    );

    const retakeRecording = React.useCallback(async () => {
        if (uploaderRef.current) {
            uploaderRef.current.abort();
            uploaderRef.current = null;
            uploaderStartPromiseRef.current = null;
        }
        transcriberRef.current?.abort();
        transcriberRef.current = null;
        const state = await recorder.retakeRecording();
        setMediaRecorderState(state);
        setShowingRetakeOverlay(false);
        setSystemAudioMissing(false);
        setRecordModalState(RECORD_MODAL_STATE.START);
    }, [recorder]);

    /**
     * The browser can stop the MediaRecorder on its own (e.g. when the recorded
     * track ends), after which it can no longer be paused or resumed. Take the
     * user to the review screen with what was captured instead of leaving them
     * on controls that no longer work.
     */
    const handleRecordingInterrupted = React.useCallback(
        async (error: unknown) => {
            ErrorReporter.capture(error, { level: 'warning' });
            try {
                await finishRecording();
                Toast.error(
                    'Your recording stopped unexpectedly. You can review and upload what was recorded.',
                );
            } catch (finishError) {
                ErrorReporter.capture(finishError);
                Toast.error('Your recording stopped unexpectedly. Please try again.');
                await retakeRecording();
            }
        },
        [finishRecording, retakeRecording],
    );

    useEffect(
        () => recorder.onUnexpectedStop((error) => void handleRecordingInterrupted(error)),
        [handleRecordingInterrupted, recorder],
    );

    const pauseRecording = React.useCallback(async () => {
        transcriberRef.current?.pause();
        try {
            const state = await recorder.pauseRecording();
            setMediaRecorderState(state);
            setRecordModalState(RECORD_MODAL_STATE.PAUSED);
        } catch (error) {
            await handleRecordingInterrupted(error);
        }
    }, [handleRecordingInterrupted, recorder]);

    const pauseRecordingButton = React.useMemo(
        () => <PauseRecordingButton key="pause-recording-button" onClick={pauseRecording} />,
        [pauseRecording],
    );

    const resumeRecording = React.useCallback(async () => {
        try {
            const state = await recorder.resumeRecording();
            transcriberRef.current?.resume();
            setMediaRecorderState(state);
            setRecordModalState(RECORD_MODAL_STATE.RECORDING);
        } catch (error) {
            await handleRecordingInterrupted(error);
        }
    }, [handleRecordingInterrupted, recorder]);

    const resumeRecordingButton = React.useMemo(
        () => <ResumeRecordingButton key="resume-recording-button" onClick={resumeRecording} />,
        [resumeRecording],
    );

    const handleCancelRetake = React.useCallback(() => {
        setShowingRetakeOverlay(false);
    }, []);

    const retakeOverlay = React.useMemo(
        () => (
            <RetakeOverlay
                key="retake-overlay"
                show={showingRetakeOverlay}
                onRetake={retakeRecording}
                onCancel={handleCancelRetake}
            />
        ),
        [showingRetakeOverlay, retakeRecording, handleCancelRetake],
    );

    const uploadRecording = React.useCallback(async () => {
        // Guard against re-entry
        if (isUploadingRef.current) {
            return;
        }
        isUploadingRef.current = true;
        setIsUploading(true);
        try {
            if (onRecordingComplete) {
                const blob = recorder.previewBlob;
                if (blob) {
                    onRecordingComplete(blob);
                } else {
                    ErrorReporter.capture(new Error('No recorded blob available on completion'));
                    Toast.error('An error occurred while saving the recording.');
                }
                return;
            }

            // Finalize the live transcription first so its words can ride along
            // on the upload payload — the backend skips its own batch
            // transcription when `transcriptWords` is present.
            let transcriptWords: TranscriptWord[] | undefined;
            const transcriber = transcriberRef.current;
            if (transcriber) {
                try {
                    transcriptWords = await transcriber.finalize(recorder.recordedDuration);
                } catch (error) {
                    ErrorReporter.capture(error);
                }
                transcriberRef.current = null;
            }

            const uploader = uploaderRef.current;
            if (!uploader) {
                ErrorReporter.capture(new Error('No uploader found when trying to upload'));
                Toast.error('An error occurred while uploading the recording.');
                return;
            }
            uploaderRef.current = null;
            uploaderStartPromiseRef.current = null;
            try {
                const result = await uploader.finalize();
                onUploadRecording({
                    gcsRef: {
                        bucket: result.bucket,
                        file_name: result.file_name,
                        asyncOperationUuid: result.asyncOperationUuid,
                    },
                    type: uploadType,
                    recordedDuration: recorder.recordedDuration,
                    recordingMeta: { fileName: result.fileName, mimeType: result.mimeType },
                    transcriptWords,
                });
            } catch (error) {
                ErrorReporter.capture(error);
                Toast.error('An error occurred while finalizing the recording upload.');
            }
        } finally {
            isUploadingRef.current = false;
            setIsUploading(false);
        }
    }, [onRecordingComplete, onUploadRecording, recorder, uploadType]);

    const uploadRecordingButton = React.useMemo(
        () => (
            <UploadRecordingButton
                key="upload-recording-button"
                onClick={uploadRecording}
                isUploading={isUploading}
            />
        ),
        [uploadRecording, isUploading],
    );

    const togglePrompter = React.useCallback(() => {
        setShowingPrompter((prev) => !prev);
    }, []);

    const prompterButton = React.useMemo(
        () => (
            <PrompterButton
                key="prompter-button"
                isPrompterShowing={showingPrompter || false}
                onClick={togglePrompter}
            />
        ),
        [showingPrompter, togglePrompter],
    );

    const prompter = React.useMemo(
        () => (
            <Prompter
                key="prompter"
                show={showingPrompter}
                onHide={togglePrompter}
                defaultText={prompterDefaultText}
                isOnLightBackground={prompterOnLightBackground}
            />
        ),
        [showingPrompter, togglePrompter, prompterDefaultText, prompterOnLightBackground],
    );

    const getRecordedDuration = React.useCallback(() => recorder.recordedDuration, [recorder]);

    const onRecordedDurationChange = React.useCallback(
        (timeMs: number) => {
            if (timeMs > maxDurationMs) {
                finishRecording();
            } else if (timeMs > warningDurationMs) {
                setDurationWarning(true);
            } else {
                setDurationWarning(false);
            }
        },
        [finishRecording, maxDurationMs, warningDurationMs],
    );

    const recorderClock = React.useMemo(
        () => (
            <RecorderClock
                key="recorder-clock"
                mediaRecorderState={mediaRecorderState}
                getRecordedDuration={getRecordedDuration}
                onRecordedDurationChange={onRecordedDurationChange}
                maxDurationMs={maxDurationMs}
            />
        ),
        [mediaRecorderState, getRecordedDuration, onRecordedDurationChange, maxDurationMs],
    );

    const durationWarning = React.useMemo(
        () => <DurationWarning key="recorder-duration-warning" show={showDurationWarning} />,
        [showDurationWarning],
    );

    const systemAudioMissingWarning = React.useMemo(
        () => (
            <SystemAudioMissingWarning
                key="system-audio-missing-warning"
                show={systemAudioMissing}
            />
        ),
        [systemAudioMissing],
    );

    const toggleSystemAudio = React.useCallback(
        (enabled: boolean) => {
            recorder.setCaptureSystemAudio(enabled);
            setIsSystemAudioEnabled(enabled);
        },
        [recorder],
    );

    const systemAudioToggleButton = React.useMemo(
        () => (
            <SystemAudioToggleButton
                key="system-audio-toggle-button"
                checked={isSystemAudioEnabled}
                onChange={toggleSystemAudio}
            />
        ),
        [isSystemAudioEnabled, toggleSystemAudio],
    );

    const screenRecordingInstructions = React.useMemo(
        () => <ScreenRecordingInstructions key="screen-recording-instructions" />,
        [],
    );

    const livePreview = React.useMemo(
        () => <LivePreview key="live-preview" stream={liveStream} />,
        [liveStream],
    );

    const recordedPreview = React.useMemo(
        () => <RecordedPreview key="recorded-preview" url={recordedPreviewUrl} />,
        [recordedPreviewUrl],
    );

    const onDeviceChange = React.useCallback(
        async (deviceType: DeviceTypes, deviceId: string) => {
            try {
                switch (deviceType) {
                    case DeviceTypes.AUDIO:
                        await recorder.connectMicrophone(deviceId);
                        break;
                    case DeviceTypes.VIDEO:
                        if (deviceId === CAMERA_OFF_DEVICE_ID) {
                            recorder.disconnectCamera();
                        } else {
                            await recorder.connectCamera(deviceId);
                        }
                        break;
                    case DeviceTypes.SCREEN:
                        await recorder.connectScreen(deviceId);
                        break;
                }
                updateComponentStateFromRecorder();
            } catch (error) {
                handleSetupError(error);
            }
        },
        [handleSetupError, recorder, updateComponentStateFromRecorder],
    );

    const deviceSelectorButton = React.useMemo(
        () => (
            <DeviceSelectorButton
                key="device-selector-button"
                availableMicrophoneDevices={availableMicrophoneDevices}
                availableCameraDevices={availableCameraDevices}
                availableScreenDevices={availableScreenDevices}
                activeMicrophoneDeviceId={activeMicrophoneDeviceId}
                activeCameraDeviceId={activeCameraDeviceId}
                activeScreenDeviceId={activeScreenDeviceId}
                onDeviceChange={onDeviceChange}
            />
        ),
        [
            activeCameraDeviceId,
            activeMicrophoneDeviceId,
            activeScreenDeviceId,
            availableCameraDevices,
            availableMicrophoneDevices,
            availableScreenDevices,
            onDeviceChange,
        ],
    );

    const deviceSelectorControls = React.useMemo(
        () => (
            <DeviceSelectorControls
                key="device-selector-controls"
                isCameraConnected={isCameraConnected}
                isMicrophoneConnected={isMicrophoneConnected}
                availableCameraDevices={availableCameraDevices}
                availableMicrophoneDevices={availableMicrophoneDevices}
                activeCameraDeviceId={activeCameraDeviceId}
                activeMicrophoneDeviceId={activeMicrophoneDeviceId}
                onDeviceChange={onDeviceChange}
                showCamera={showCameraDeviceSelector}
                allowCameraOff={allowCameraOff}
                microphoneToggle={showSystemAudioToggle ? systemAudioToggleButton : undefined}
                className={allowCameraOff || showSystemAudioToggle ? 'w-50 mx-auto' : 'w-75'}
            />
        ),
        [
            activeCameraDeviceId,
            activeMicrophoneDeviceId,
            availableCameraDevices,
            availableMicrophoneDevices,
            isCameraConnected,
            isMicrophoneConnected,
            onDeviceChange,
            showCameraDeviceSelector,
            allowCameraOff,
            showSystemAudioToggle,
            systemAudioToggleButton,
        ],
    );

    useEffect(() => {
        /**
         * Handle browser stop screen sharing event
         */
        const handleBrowserStopSharing = () => {
            if (mediaRecorderState !== 'inactive') {
                finishRecording();
            }
        };
        RecorderEventEmitter.on(RECORDER_EVENTS.SCREEN_SHARING_STOPPED, handleBrowserStopSharing);
        return () => {
            RecorderEventEmitter.off(
                RECORDER_EVENTS.SCREEN_SHARING_STOPPED,
                handleBrowserStopSharing,
            );
        };
    }, [finishRecording, mediaRecorderState]);

    useEffect(() => {
        /**
         * Handle component unmout (modal closed)
         */
        return () => {
            if (uploaderRef.current) {
                uploaderRef.current.abort();
                uploaderRef.current = null;
                uploaderStartPromiseRef.current = null;
            }
            transcriberRef.current?.abort();
            transcriberRef.current = null;
            recorder.destroy();
        };
    }, [recorder]);

    return {
        recordModalState,
        startPage,
        checkSupportError,
        checkPermissionsError,
        screenRecordingInstructions,
        prompterButton,
        prompter,
        recorderClock,
        durationWarning,
        systemAudioMissingWarning,
        liveStream,
        activeMicrophoneDeviceId,
        livePreview,
        recordedPreview,
        recordedPreviewUrl,
        deviceSelectorButton,
        deviceSelectorControls,
        startRecordingButton,
        pauseRecordingButton,
        resumeRecordingButton,
        finishRecordingButton,
        retakeRecordingButton,
        retakeOverlay,
        uploadRecordingButton,
        countdownTimer,
    };
};
